/**
 * useFlashUserRow.ts
 * The Phase-2 analogue of useFlashBooking (Phase-1): clicking a person's name/avatar in
 * the Task Weekly Schedule scrolls their row in the Employee Weekly Capacity grid into
 * view and PULSES it so the planner can see that person's scheduled days + percentages
 * (Gigi, 2026-06-25). Works for all three task views (Classic / Board / Roster).
 *
 * Capacity rows carry a `data-flash-user="<userKey>"` attribute, queried from the shared
 * rail container. Uses the Web Animations API (a brightness + glow pulse — table rows
 * can't take a box-shadow ring under border-collapse, and a transform would distort the
 * table, so we pulse `filter`) ×3 (~1.7s). No leftover inline styles to clean up.
 */
import { useCallback } from 'react';

export function useFlashUserRow(
  containerRef: React.RefObject<HTMLElement | null>,
): (userKey: string, color: string) => void {
  return useCallback(
    (userKey: string, color: string) => {
      const el = containerRef.current?.querySelector<HTMLElement>(
        `[data-flash-user="${userKey}"]`,
      );
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      el.animate(
        [
          { filter: 'brightness(1)' },
          { filter: `brightness(1.22) drop-shadow(0 0 5px ${color})` },
          { filter: 'brightness(1)' },
        ],
        { duration: 560, iterations: 3, easing: 'ease-in-out' },
      );
    },
    [containerRef],
  );
}
